import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { usePipeline } from '../../../hooks/queries/usePipeline';
import type { Application, ApplicationStatus } from '@applicant-reviewer/api';

interface ManagerPipelineProps {
  onNavigate?: (route: string) => void;
  onOpenApplicant?: (applicant: Application, applicants: Application[]) => void;
  onOpenInterview?: (candidateId: string) => void;
}

type SortField = 'candidate' | 'date' | 'schedule' | 'ai';
type SortOrder = 'asc' | 'desc';

interface SortState {
  field: SortField;
  order: SortOrder;
}

function getApplicantName(app: Application): string {
  return [app.contactInfo?.firstName, app.contactInfo?.lastName].filter(Boolean).join(' ') || 'Unknown';
}

const PIPELINE_TABS = [
  { id: 'submitted', label: 'New', countSource: 'submitted' },
  { id: 'interview', label: 'Interview', countSource: 'interview' },
  { id: 'offered', label: 'Hire', countSource: 'offered' },
  { id: 'benched', label: 'Bench', countSource: 'benched' },
  { id: 'rejected', label: 'Reject', countSource: 'rejected' },
] as const;

export default function ManagerPipeline({ onNavigate, onOpenApplicant, onOpenInterview }: ManagerPipelineProps) {
  const [activeTab, setActiveTab] = useState<(typeof PIPELINE_TABS)[number]['id']>('submitted');
  const [sortState, setSortState] = useState<SortState>({ field: 'date', order: 'desc' });
  const [isScoresMenuOpen, setIsScoresMenuOpen] = useState(false);

  const { data: pipelineData, isLoading, isError } = usePipeline();

  const activeApplicants: Application[] = pipelineData?.pipeline[activeTab] ?? [];

  const sortedApplicants = useMemo(() => {
    return [...activeApplicants].sort((a, b) => {
      let comparison = 0;
      switch (sortState.field) {
        case 'candidate':
          comparison = getApplicantName(a).localeCompare(getApplicantName(b));
          break;
        case 'date':
          comparison = new Date(a.submittedAt ?? a.createdAt).getTime() - new Date(b.submittedAt ?? b.createdAt).getTime();
          break;
        case 'schedule':
          comparison = (a.scheduleFit ?? 0) - (b.scheduleFit ?? 0);
          break;
        case 'ai':
          comparison = (a.aiScore ?? 0) - (b.aiScore ?? 0);
          break;
      }
      return sortState.order === 'asc' ? comparison : -comparison;
    });
  }, [sortState, activeApplicants]);

  const handleHeaderClick = (field: 'candidate' | 'date') => {
    setSortState(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleScoreSort = (field: 'schedule' | 'ai', order: 'asc' | 'desc') => {
    setSortState({ field, order });
    setIsScoresMenuOpen(false);
  };

  const handleRowClick = (applicant: Application) => {
    if (activeTab === 'interview') {
      if (onOpenInterview) {
        onOpenInterview(applicant.applicationId);
      }
    } else {
      if (onOpenApplicant) {
        onOpenApplicant(applicant, sortedApplicants);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6 flex items-center justify-center">
        <p className="text-sm text-gray-400">Loading pipeline…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6 flex items-center justify-center">
        <p className="text-sm text-red-500">Failed to load pipeline. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6">
      {/* Pipeline Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="flex overflow-x-auto border-b border-gray-100">
          {PIPELINE_TABS.map((tab) => {
            const count = pipelineData?.counts[tab.countSource as ApplicationStatus] ?? 0;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-3 text-sm font-semibold ${
                  activeTab === tab.id ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-400'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className="inline-flex items-center justify-center h-5 min-w-[24px] text-xs rounded-full bg-gray-100 text-gray-600">
                      {count}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Table Header */}
        <div className="flex items-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
          <div
            className="flex-1 cursor-pointer hover:text-gray-700 select-none"
            onClick={() => handleHeaderClick('candidate')}
          >
            Candidate {sortState.field === 'candidate' && (sortState.order === 'desc' ? '↓' : '↑')}
          </div>
          <div
            className="w-32 text-center cursor-pointer hover:text-gray-700 select-none"
            onClick={() => handleHeaderClick('date')}
          >
            Date {sortState.field === 'date' && (sortState.order === 'desc' ? '↓' : '↑')}
          </div>
          <div className="w-40 flex items-center justify-end relative">
            <button
              type="button"
              onClick={() => setIsScoresMenuOpen((prev) => !prev)}
              className="flex items-center gap-1 hover:text-gray-700"
            >
              SCORES {['schedule', 'ai'].includes(sortState.field) && (sortState.order === 'desc' ? '↓' : '↑')}
            </button>
            {isScoresMenuOpen && (
              <div className="absolute right-0 top-8 z-10 w-56 bg-white border border-gray-200 rounded-xl shadow-lg text-left text-sm py-1">
                <button
                  onClick={() => handleScoreSort('schedule', 'desc')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                >
                  Schedule (Best → Worst)
                </button>
                <button
                  onClick={() => handleScoreSort('schedule', 'asc')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                >
                  Schedule (Worst → Best)
                </button>
                <button
                  onClick={() => handleScoreSort('ai', 'desc')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                >
                  AI Fit (Best → Worst)
                </button>
                <button
                  onClick={() => handleScoreSort('ai', 'asc')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                >
                  AI Fit (Worst → Best)
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Table Rows */}
        <div>
          {sortedApplicants.map((applicant, idx) => (
            <button
              key={applicant.applicationId}
              type="button"
              onClick={() => handleRowClick(applicant)}
              className={`flex items-center px-5 h-[54px] w-full hover:bg-gray-50 cursor-pointer ${
                idx < sortedApplicants.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold text-gray-900">{getApplicantName(applicant)}</p>
              </div>
              <div className="w-32 text-center text-xs text-gray-500">
                {format(new Date(applicant.submittedAt ?? applicant.createdAt), 'LLL d')}
              </div>
              <div className="w-40 flex flex-col items-end gap-1">
                {/* Schedule Fit - Dark Orange */}
                <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-600 to-orange-500"
                    style={{ width: `${applicant.scheduleFit ?? 0}%` }}
                  />
                </div>
                {/* AI Score - Purple */}
                <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-600 to-purple-500"
                    style={{ width: `${applicant.aiScore ?? 0}%` }}
                  />
                </div>
              </div>
            </button>
          ))}

          {sortedApplicants.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-gray-400">
              No applicants in this stage.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
